import { useState, useMemo } from "react";
import { Loader2, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { waLink, telLink, track, newIdempotencyKey } from "../../lib/site";

const utms = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "", utm_term: p.get("utm_term") || "",
    utm_content: p.get("utm_content") || "",
  };
};

export default function ContactForm() {
  const { settings } = useSite();
  const idemKey = useMemo(newIdempotencyKey, []);
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "", preferred_contact: "call" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/contact", {
        ...form, page: window.location.pathname, idempotency_key: idemKey, ...utms(),
      });
      track("lead_submit", { lead_id: data.lead_id });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-form-success" className="card-site p-8 sm:p-10 text-center h-full flex flex-col justify-center">
        <span className="mx-auto w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={28} /></span>
        <h3 className="mt-5 font-heading text-2xl font-bold text-brand-dark">Thank You!</h3>
        <p className="mt-2 text-sm text-brand-muted leading-relaxed">Your enquiry has been submitted successfully.<br />Our BharatBest Fintech team will review your enquiry and contact you.</p>
        <p className="mt-4 text-sm text-brand-muted">Lead ID: <span className="font-heading font-extrabold text-brand-red" data-testid="contact-lead-id">{done.lead_id}</span></p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a href={waLink(settings)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { lead_id: done.lead_id })}
            data-testid="contact-success-whatsapp" className="btn-outline !border-[#1FA855]/40 !text-[#1FA855]"><MessageCircle size={15} /> WhatsApp Us</a>
          <a href={telLink(settings)} onClick={() => track("phone_click", { lead_id: done.lead_id })}
            data-testid="contact-success-call" className="btn-outline"><Phone size={15} /> Call Now</a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="card-site p-6 sm:p-8 h-full">
      <h3 className="font-heading text-xl sm:text-2xl font-bold text-brand-dark mb-6">Send Us an Enquiry</h3>
      <div className="grid gap-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <input required data-testid="contact-name-input" className="input-site" placeholder="Full Name *" value={form.name} onChange={set("name")} aria-label="Full name" maxLength={120} />
          <input required data-testid="contact-phone-input" className="input-site" placeholder="Contact Number *" value={form.phone} onChange={set("phone")} inputMode="tel" aria-label="Contact number" maxLength={15} />
        </div>
        <input required data-testid="contact-email-input" type="email" className="input-site" placeholder="Email Address *" value={form.email} onChange={set("email")} aria-label="Email" />
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="contact-subject-input" className="input-site" placeholder="Subject (e.g. Personal Loan Enquiry)" value={form.subject} onChange={set("subject")} aria-label="Subject" maxLength={150} />
          <select data-testid="contact-preferred-select" className="input-site" value={form.preferred_contact} onChange={set("preferred_contact")} aria-label="Preferred contact method">
            <option value="call">Preferred: Phone Call</option>
            <option value="whatsapp">Preferred: WhatsApp</option>
            <option value="email">Preferred: Email</option>
          </select>
        </div>
        <textarea required data-testid="contact-message-input" className="input-site min-h-[110px]" placeholder="Your Message *" value={form.message} onChange={set("message")} aria-label="Message" maxLength={2000} />
      </div>
      <p className="mt-4 text-xs leading-relaxed text-brand-muted" data-testid="contact-consent-text">{settings.consent_text}</p>
      <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-primary mt-5 w-full sm:w-auto">
        {loading ? <Loader2 size={16} className="animate-spin" /> : "Submit Enquiry"}
      </button>
    </form>
  );
}
